import { useEffect, useState } from "react";
import { api, orderPdfUrl } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import NegotiationPanel from "@/components/shared/NegotiationPanel";
import MessageThread from "@/components/shared/MessageThread";

const STATUSES = ["pending", "negotiating", "agreed", "confirmed", "in_production", "shipped", "cancelled"];

const statusColor = {
    pending: "bg-yellow-100 text-yellow-900 border-yellow-300",
    negotiating: "bg-orange-50 text-orange-900 border-orange-400",
    agreed: "bg-purple-50 text-purple-900 border-purple-400",
    confirmed: "bg-blue-50 text-blue-900 border-blue-300",
    in_production: "bg-neutral-100 text-neutral-900 border-neutral-400",
    shipped: "bg-green-50 text-green-900 border-green-400",
    cancelled: "bg-red-50 text-red-900 border-red-300",
};

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [openId, setOpenId] = useState(null);

    const load = async () => {
        const r = await api.get("/orders");
        setOrders(r.data);
    };

    useEffect(() => {
        load();
        api.post("/notifications/mark-orders-seen", {}).catch(() => {});
    }, []);

    const updateStatus = async (id, s) => {
        try {
            await api.put(`/orders/${id}/status`, { status: s });
            toast.success("Status updated");
            load();
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
            <div className="flex items-end justify-between mb-8">
                <div>
                    <div className="overline text-neutral-500">Client Orders</div>
                    <h1 className="font-display text-5xl tracking-tight leading-none mt-2">Orders</h1>
                </div>
                <div className="font-body text-sm text-neutral-500">{orders.length} total</div>
            </div>

            {orders.length === 0 && (
                <div className="border border-dashed border-border p-12 text-center">
                    <div className="overline text-neutral-500">Empty</div>
                    <div className="font-display text-2xl mt-2">No orders yet</div>
                </div>
            )}

            <div className="space-y-3">
                {orders.map((o) => (
                    <div key={o.id} className="border border-border" data-testid={`order-row-${o.id}`}>
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 p-5 items-center">
                            <div>
                                <div className="font-mono text-xs text-neutral-500">{o.order_number}</div>
                                <div className="font-body text-xs text-neutral-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                            </div>
                            <div className="md:col-span-2">
                                <div className="overline text-neutral-500">Client</div>
                                <div className="font-body text-sm mt-1">{o.client_company || o.client_name}</div>
                                <div className="font-body text-xs text-neutral-500">ID {o.client_login_id}</div>
                            </div>
                            <div>
                                <div className="overline text-neutral-500">Pieces</div>
                                <div className="font-body text-sm mt-1 tabular-nums">{o.items.reduce((s,i)=>s+i.quantity,0).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="overline text-neutral-500">Total</div>
                                <div className="font-display text-2xl tabular-nums leading-none mt-1">£{o.total.toFixed(2)}</div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                                    <SelectTrigger className={`rounded-none uppercase text-[10px] tracking-widest font-semibold border ${statusColor[o.status] || ""}`} data-testid={`status-select-${o.id}`}>
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="uppercase tracking-widest text-[10px]">{s.replace("_", " ")}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <button onClick={() => setOpenId(openId === o.id ? null : o.id)} className="text-xs uppercase tracking-widest text-neutral-600 hover:text-black text-left" data-testid={`toggle-details-${o.id}`}>
                                    {openId === o.id ? "Hide details" : "View details"}
                                </button>
                            </div>
                        </div>
                        {openId === o.id && (
                            <div className="border-t border-border bg-neutral-50 p-5 space-y-4">
                                <NegotiationPanel order={o} role="admin" onChanged={load}/>
                                <MessageThread threadType="order" threadId={o.id}/>
                                {["confirmed", "in_production", "shipped"].includes(o.status) && (
                                    <a
                                        href={orderPdfUrl(o.id)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 border border-black bg-white text-black px-4 h-10 uppercase tracking-widest text-[10px] font-semibold hover:bg-black hover:text-white transition-colors"
                                        data-testid={`admin-order-pdf-${o.id}`}
                                    >
                                        <FileText className="h-3.5 w-3.5" strokeWidth={1.5}/> Download Production PDF
                                    </a>
                                )}
                                {o.notes && (
                                    <div>
                                        <div className="overline text-neutral-500 mb-1">Client Notes</div>
                                        <div className="font-body text-sm">{o.notes}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
